import router from "@/routers";
import { defineStore } from "pinia";
import { TabsMenuProps, TabsState } from "@/stores/interface";
import { useKeepAliveStore } from "./keepAlive";
import { RouteLocation } from "vue-router";
import piniaPersistConfig from "@/stores/helper/persist";
import { MAX_TAG_NUMBER } from "@/config";
import { WEBSOCKET_READY_STATE } from "@/meta/common";
import { useUserStore } from "@/stores/modules/user";
import { nextTick } from "vue";

let connected = false;
let failure = false;

export const useTabsStore = defineStore({
    id: "zhongsheng-tabs",
    state: (): TabsState => ({
        tabsMenuList: []
    }),
    actions: {
        // Add Tabs
        async addTabs(tabItem: TabsMenuProps) {
            if (this.tabsMenuList.every(item => item.path !== tabItem.path)) {
                if (this.tabsMenuList.some(item => item.name === tabItem.name)) {
                    const index = this.tabsMenuList.findIndex(item => item.name === tabItem.name);
                    this.tabsMenuList[index] = tabItem;
                } else {
                    this.tabsMenuList.push(tabItem);
                }
            }
            if (this.tabsMenuList.length > MAX_TAG_NUMBER) await this.removeLimitExceededTabs();
        },
        // Remove Tabs
        async removeTabs(tabPath: string, callback?: Function, isCurrent: boolean = true, closeCurrent: boolean = false) {
            const tabsMenuList = this.tabsMenuList;
            if (isCurrent) {
                tabsMenuList.forEach((item, index) => {
                    if (item.path !== tabPath) return;
                    const nextTab = tabsMenuList[index + 1] || tabsMenuList[index - 1];
                    if (!nextTab) return;
                    if (!callback) router.push(nextTab.path);
                });
            }
            if (!callback && closeCurrent) await router.push(tabsMenuList[0].path);
            this.tabsMenuList = tabsMenuList.filter(item => item.path !== tabPath);
            if (callback) callback();
        },
        // 按名称删 tab 新
        async removeTabsByName(tabName: string, callback?: Function, isCurrent: boolean = true, closeCurrent: boolean = false) {
            const tabsMenuList = this.tabsMenuList;
            if (isCurrent) {
                tabsMenuList.forEach((item, index) => {
                    if (item.name !== tabName) return;
                    const nextTab = tabsMenuList[index + 1] || tabsMenuList[index - 1];
                    if (!nextTab) return;
                    if (!callback) router.push(nextTab.path);
                });
            }
            if (!callback && closeCurrent) await router.push(tabsMenuList[0].path);
            this.tabsMenuList = tabsMenuList.filter(item => item.name !== tabName);
            if (callback) callback();
        },
        // 按照配置模块MAX_TAG_NUMBER删除超出数量的tab
        async removeLimitExceededTabs(currentPath?: string) {
            const maxTagNumber = MAX_TAG_NUMBER;
            if (!maxTagNumber) return;
            const tabsMenuList = this.tabsMenuList;
            this.tabsMenuList = tabsMenuList.slice(-maxTagNumber);
            const keepAliveStore = useKeepAliveStore();
            await keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
            if (currentPath && !this.tabsMenuList.every(item => item.path === currentPath))
                await router.push(this.tabsMenuList[this.tabsMenuList.length - 1].path);
        },
        // Close Tabs On Side
        async closeTabsOnSide(path: string, type: "left" | "right") {
            const currentIndex = this.tabsMenuList.findIndex(item => item.path === path);
            if (currentIndex !== -1) {
                this.tabsMenuList = this.tabsMenuList.filter((item, index) => {
                    const flag = type === "left" ? index >= currentIndex : index <= currentIndex;
                    return flag || !item.close;
                });
            }
            const keepAliveStore = useKeepAliveStore();
            await keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
        },
        // Close MultipleTab
        async closeMultipleTab(tabsMenuValue?: string) {
            this.tabsMenuList = this.tabsMenuList.filter(item => {
                return item.path === tabsMenuValue || !item.close || item.path === router.currentRoute.value.path;
            });
            const keepAliveStore = useKeepAliveStore();
            await keepAliveStore.setKeepAliveName(this.tabsMenuList.map(item => item.name));
        },
        async closeAllTab() {
            this.tabsMenuList = [];
            const keepAliveStore = useKeepAliveStore();
            await keepAliveStore.setKeepAliveName([]);
        },
        // Set Tabs
        async setTabs(tabsMenuList: TabsMenuProps[]) {
            this.tabsMenuList = tabsMenuList;
        },
        // 关闭tab
        async closeTab(route: RouteLocation, callback?: Function) {
            await this.removeTabs(route.fullPath ?? route.path, callback);
            const keepAliveStore = useKeepAliveStore();
            await keepAliveStore.removeKeepAliveName(route.name as string);
        },
        connectWebsocket() {
            const that = this;
            if (connected || failure) return;
            connected = true;
            let WS: WebSocket;

            const user = useUserStore();
            const auth = `authorization=${user.token}`;
            console.log("WS地址---", `${import.meta.env.VITE_WS_URL}?${auth}`);

            try {
                WS = new WebSocket(`${import.meta.env.VITE_WS_URL}?${auth}`);

                WS.onopen = function () {
                    console.log("建立WS成功---", WS.readyState);
                };
                WS.onmessage = async function (res: Record<string, unknown>) {
                    const data = JSON.parse(res.data);
                    if (data?.type === 1) {
                        await nextTick(() => {
                            user.refreshUserInfo();
                        });
                        await that.closeAllTab();
                    }
                };
                WS.onclose = function (event: object) {
                    console.log("WS关闭链接---", event);
                    connected = false;
                };
                WS.onerror = function (err: object) {
                    failure = true;
                    console.log(err);
                };

                // 每25秒发送一个心跳信息
                const loopConnection = () => {
                    setInterval(() => {
                        if (WS.readyState === WEBSOCKET_READY_STATE.OPEN.code) {
                            WS.send(JSON.stringify({ heartBeat: "HEARTBEAT" }));
                        } else {
                            connected = false;
                        }
                    }, 25000);
                };
                loopConnection();
            } catch (err) {
                failure = true;
                console.error(err);
            }
        }
    },
    persist: piniaPersistConfig("zhongsheng-tabs")
});
